import z from "zod";
import { create } from "zustand";

import {
  AudienceSearchFilterType,
  GetAudienceSearchUserFilterQuery,
} from "~frontend/__generated__/graphql";
import { TargetingFormSchema } from "~frontend/components/audienceFilteringForm/schema";

const _AudienceSearchFilterTargetingFormSchema = TargetingFormSchema.extend({
  id: z.string().nullish(),
  name: z.string().optional(),
  contactCount: z.number().nullish(),
  optedOutContactCount: z.number().nullish(),
  type: z.nativeEnum(AudienceSearchFilterType).nullish(),
});

export type AudienceSearchFilterTargetingForm = z.infer<
  typeof _AudienceSearchFilterTargetingFormSchema
>;

export interface AudienceState {
  activeAudienceSearchFilter:
    | GetAudienceSearchUserFilterQuery["getAudienceSearchUserFilter"]
    | null;
  setActiveAudienceSearchFilter: (
    filter: GetAudienceSearchUserFilterQuery["getAudienceSearchUserFilter"]
  ) => void;
  currentFilter: AudienceSearchFilterTargetingForm;
  setFilter: (filter: AudienceSearchFilterTargetingForm) => void;
  clearFilters: () => void;

  isFilterEmpty: () => boolean;

  isEditingExistingFilter: boolean;
  setIsEditingExistingFilter: (newValue: boolean) => void;
  toggleIsEditingExistingFilter: () => void;
  refetchSearchResults: () => Promise<any>;
  setRefetchSearchResults: (fn: () => Promise<any>) => void;
  searchJobIsRunning: boolean;
  setSearchJobIsRunning: (newValue: boolean) => void;
}

export const useAudience = create<AudienceState>()((set, getState) => ({
  activeAudienceSearchFilter: null,
  setActiveAudienceSearchFilter: filter =>
    set({ activeAudienceSearchFilter: filter }),
  currentFilter: {
    targetGroups: [],
    name: "",
    type: AudienceSearchFilterType.UserSearch,
  },
  setFilter: filter => set({ currentFilter: filter }),
  isFilterEmpty: () => {
    const { currentFilter } = getState();
    return (
      !currentFilter.targetGroups || currentFilter.targetGroups.length === 0
    );
  },
  clearFilters: () =>
    set({
      currentFilter: {
        targetGroups: [],
        name: "",
        type: AudienceSearchFilterType.UserSearch,
      },
      activeAudienceSearchFilter: null,
      isEditingExistingFilter: false,
    }),
  isEditingExistingFilter: false,
  setIsEditingExistingFilter: newValue =>
    set({ isEditingExistingFilter: newValue }),
  toggleIsEditingExistingFilter: () => {
    const isEditing = getState().isEditingExistingFilter;
    return set({ isEditingExistingFilter: !isEditing });
  },
  refetchSearchResults: async () => {},
  setRefetchSearchResults: (fn: () => Promise<any>) =>
    set({ refetchSearchResults: fn }),
  searchJobIsRunning: false,
  setSearchJobIsRunning: (newValue: boolean) =>
    set({ searchJobIsRunning: newValue }),
}));
